import { useQuery } from "@tanstack/react-query";
import { NavLink, Outlet } from "react-router-dom";

import { api } from "../lib/api";
import { useDemo } from "../lib/demo";
import { useLogout, useSession } from "../hooks/useSession";
import type { Alert, Notification, ProductBrief } from "../lib/types";

/**
 * Znak Folwark Labs — świnka w okularach laboratoryjnych.
 * Źródło: `design/brand/pig-current.svg`. Maska wycina te partie, które
 * w oryginale były kremowe, więc znak jest naprawdę jednokolorowy i siada
 * na dowolnym tle. `id` maski musi być unikalne w obrębie dokumentu —
 * ekran logowania ma własną kopię z innym identyfikatorem.
 */
function Znak() {
  return (
    <svg viewBox="38 15 151 153" aria-hidden="true" focusable="false">
      <mask id="znak-hq" maskUnits="userSpaceOnUse" x="0" y="0" width="200" height="200">
        <rect width="200" height="200" fill="#fff" />
        <ellipse cx="100" cy="130" rx="30" ry="22" fill="#000" />
        <ellipse cx="91" cy="130" rx="4.6" ry="7" fill="#fff" />
        <ellipse cx="109" cy="130" rx="4.6" ry="7" fill="#fff" />
        <path d="M62 96 L44 90" fill="none" stroke="#000" strokeWidth="4" strokeLinecap="round" />
        <path d="M138 96 L156 90" fill="none" stroke="#000" strokeWidth="4" strokeLinecap="round" />
        <circle cx="77" cy="99" r="15" fill="none" stroke="#000" strokeWidth="4" />
        <circle cx="123" cy="99" r="15" fill="none" stroke="#000" strokeWidth="4" />
        <circle cx="77" cy="99" r="6" fill="#000" />
        <circle cx="123" cy="99" r="6" fill="#000" />
        <path d="M92 99 Q100 94 108 99" fill="none" stroke="#000" strokeWidth="4" strokeLinecap="round" />
      </mask>
      <g mask="url(#znak-hq)" fill="currentColor">
        <path d="M52 82 L48 40 Q47 32 55 36 L90 58 Z" />
        <path d="M148 82 L152 40 Q153 32 145 36 L110 58 Z" />
        <path d="M40 104 C40 71 67 52 100 52 C133 52 160 71 160 104 C160 141 133 166 100 166 C67 166 40 141 40 104 Z" />
        <circle cx="161" cy="48" r="6" />
        <circle cx="175" cy="32" r="4" />
        <circle cx="184" cy="20" r="2.6" />
      </g>
    </svg>
  );
}

export function Shell() {
  const session = useSession();
  const logout = useLogout();
  const { includeDemo, setIncludeDemo, scoped, key } = useDemo();

  // Boczna lista pokazuje wszystkie produkty niezależnie od przełącznika —
  // ukryte demo ma być odcięte od liczb, a nie schowane przed właścicielem.
  const products = useQuery({
    queryKey: ["products", "wszystkie"],
    queryFn: () => api.get<{ products: ProductBrief[] }>("/products"),
  });

  const alerts = useQuery({
    queryKey: ["alerts", "open", key],
    queryFn: () => api.get<{ alerts: Alert[] }>(scoped("/alerts")),
    refetchInterval: 5 * 60_000,
  });

  // Skrzynka odpytuje się częściej niż alerty: przypisanie zadania jest
  // wiadomością do człowieka i ma dogonić go w trakcie pracy, a nie po godzinie.
  const inbox = useQuery({
    queryKey: ["notifications"],
    queryFn: () => api.get<{ notifications: Notification[]; unread: number }>("/notifications"),
    refetchInterval: 2 * 60_000,
  });

  const openAlerts = alerts.data?.alerts.length ?? 0;
  const unread = inbox.data?.unread ?? 0;
  const hasDemo = products.data?.products.some((product) => product.is_demo) ?? false;

  return (
    <div className="shell">
      <aside className="sidebar">
        <NavLink to="/" className="sidebar__brand">
          <Znak />
          <span>
            Folwark<span> HQ</span>
          </span>
        </NavLink>

        <div className="sidebar__group">
          <p className="sidebar__label">Portfolio</p>
          <NavLink to="/" end className="navlink">
            Przegląd
          </NavLink>
          <NavLink to="/projekty" className="navlink">
            Projekty
          </NavLink>
          <NavLink to="/sprzedaz" className="navlink">
            Sprzedaż
          </NavLink>
          <NavLink to="/koszty" className="navlink">
            Koszty
          </NavLink>
          <NavLink to="/alerty" className="navlink">
            Alerty
            {openAlerts > 0 && <span className="navlink__badge">{openAlerts}</span>}
          </NavLink>
          <NavLink to="/bledy" className="navlink">
            Błędy
          </NavLink>
          <NavLink to="/wdrozenie" className="navlink">
            Wdrożenie
          </NavLink>
          <NavLink to="/eksport" className="navlink">
            Eksport
          </NavLink>
          <NavLink to="/zespol" className="navlink">
            Zespół
          </NavLink>
        </div>

        <div className="sidebar__group">
          <p className="sidebar__label">Ty</p>
          <NavLink to="/skrzynka" className="navlink">
            Skrzynka
            {unread > 0 && <span className="navlink__badge">{unread}</span>}
          </NavLink>
        </div>

        <div className="sidebar__group">
          <p className="sidebar__label">Produkty</p>
          {products.data?.products.map((product) => (
            <NavLink key={product.slug} to={`/produkt/${product.slug}`} className="navlink">
              <i
                className="navlink__dot"
                style={{ color: product.accent_color }}
                aria-hidden="true"
              />
              {product.name}
              {product.is_demo && <span className="navlink__tag">demo</span>}
            </NavLink>
          ))}
        </div>

        {hasDemo && (
          <div className="sidebar__group">
            <p className="sidebar__label">Dane</p>
            <label className="switch">
              <input
                type="checkbox"
                checked={includeDemo}
                onChange={(event) => setIncludeDemo(event.target.checked)}
              />
              <span className="switch__track" aria-hidden="true">
                <span className="switch__knob" />
              </span>
              <span className="switch__label">Wliczaj dane demo</span>
            </label>
          </div>
        )}

        <div className="sidebar__foot">
          <div>{session.data?.email}</div>
          <div className="dim">{session.data?.organization.name}</div>
          <button type="button" onClick={() => logout.mutate()}>
            Wyloguj
          </button>
        </div>
      </aside>

      <main className="main">
        <Outlet />
      </main>
    </div>
  );
}
